package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.QaSeverity
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LCopyIcon
import dev.lingvo.site.ds.LIconButton
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.chordKeys
import dev.lingvo.site.ds.monoFont
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tPlural

internal data class EditorPluralPanelModel(
    val category: PluralCategory,
    val categoryLabel: String,
    val example: String,
    val status: String,
    val source: String,
    val target: String,
    val preview: String,
    val findings: List<InlineQaFinding>,
)

internal data class EditorSourceOnlyPluralModel(
    val category: PluralCategory,
    val categoryLabel: String,
    val source: String,
)

internal data class EditorPluralPresentation(
    val requiredPanels: List<EditorPluralPanelModel>,
    val sourceOnlyPanels: List<EditorSourceOnlyPluralModel>,
)

/**
 * How much of a plural key is done, counted in the target language's own forms.
 *
 * The noun agrees with the total, and the total is a CLDR category count: Japanese has one form, so
 * the hand-suffixed version drew "1 of 1 forms complete" on the first key it rendered — the tool
 * built to get plurals right getting one wrong about itself. `{done}` is substituted after the form
 * is chosen so a translation can put the two numbers where its own grammar wants them.
 */
internal fun pluralCompletionLabel(
    categories: List<PluralCategory>,
    draft: EditorDraft,
): String {
    val complete = categories.count { draft.pluralForms[it].orEmpty().isNotBlank() }
    return tPlural(
        "editor.editor_field_presentation.count.form_complete",
        categories.size,
        "{done} of {count} form complete",
        "{done} of {count} forms complete",
        "done" to complete,
    )
}

internal fun editorPluralPanels(
    row: dev.lingvo.shared.KeyRow,
    draft: EditorDraft,
    locale: String,
    findings: List<InlineQaFinding>,
): EditorPluralPresentation {
    val categories = dev.lingvo.shared.pluralCategoriesFor(locale).toList()
    val sourceForms = row.source.pluralForms.orEmpty()
    return EditorPluralPresentation(
        requiredPanels = categories.map { category ->
            val target = draft.pluralForms[category].orEmpty()
            EditorPluralPanelModel(
                category = category,
                categoryLabel = category.displayName(),
                example = pluralExample(category, locale),
                status = if (target.isBlank()) "missing" else "complete",
                source = row.sourceFor(category),
                target = target,
                preview = pluralPreview(target, category),
                findings = pluralQaForCategory(findings, category),
            )
        },
        sourceOnlyPanels = sourceForms.keys
            .filterNot(categories::contains)
            .map { category ->
                EditorSourceOnlyPluralModel(
                    category = category,
                    categoryLabel = category.displayName(),
                    source = sourceForms[category].orEmpty(),
                )
            },
    )
}

/**
 * The controls that act on the source as a whole: the locale tag, the machine-translate button
 * with its `Alt T` hint, and Copy source.
 *
 * Extracted so [EditorSourceField] (one text, one row, for a singular key) and the plural stack's
 * single row above every form (`EditorPluralFields`) share one copy of these three controls
 * instead of each owning their own. A plural key used to mount this row once per CLDR category —
 * six times on an Arabic key — with every "Translate with <provider>" button firing the identical
 * row-level request (`EditorPanelStore.translate` takes a `KeyRow`, not a category) and all six
 * spinning together.
 */
@Composable
internal fun EditorSourceActionRow(
    onCopySource: () -> Unit,
    localeLabel: String? = null,
    /**
     * The remembered-or-default provider's name, or null when the project has none enabled — in
     * which case the button is not drawn rather than drawn pointing at nothing.
     */
    machineProviderLabel: String? = null,
    onMachineTranslate: (() -> Unit)? = null,
    machineTranslating: Boolean = false,
) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        localeLabel?.let { label ->
            Div(EditorMetaStyle.toModifier().monoFont().toAttrs()) { Text(label) }
        }
        Spacer()
        if (machineProviderLabel != null && onMachineTranslate != null) {
            Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                LButton(
                    label = t("editor.editor_field_presentation.generate_suggestion", "Generate suggestion"),
                    onClick = onMachineTranslate,
                    modifier = EditorCoarseTargetStyle.toModifier()
                        .attr("data-editor-coarse-target", "tool"),
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    loading = machineTranslating,
                    leadingIcon = { LSparkleIcon() },
                    accessibleLabel = tf(
                        "editor.editor_field_presentation.generate_suggestion_with_provider",
                        "Generate suggestion with {provider}",
                        "provider" to machineProviderLabel,
                    ),
                )
                // One cap per key: "Alt T" in a single `LKbd` drew as one wide slab and was read
                // out as a single token by a screen reader.
                LChord(chordKeys("Alt T"))
            }
        }
        LIconButton(
            label = t("editor.editor_field_presentation.copy_source", "Copy source"),
            onClick = onCopySource,
            modifier = EditorCoarseTargetStyle.toModifier()
                .attr("data-editor-coarse-target", "tool"),
            size = LButtonSize.SM,
        ) {
            LCopyIcon()
        }
    }
}

@Composable
internal fun EditorSourceField(
    value: String,
    direction: String,
    onCopySource: () -> Unit,
    localeLabel: String? = null,
    warningPlaceholders: Set<String> = emptySet(),
    /** BCP-47 tag of the source locale. Picks the script face; see FontStack. */
    lang: String? = null,
    machineProviderLabel: String? = null,
    onMachineTranslate: (() -> Unit)? = null,
    machineTranslating: Boolean = false,
) {
    Column(
        SourceSurfaceStyle.toModifier()
            .fillMaxWidth()
            .minWidth(0.px)
            .attr("data-editor-source", "true"),
    ) {
        EditorSourceActionRow(
            onCopySource = onCopySource,
            localeLabel = localeLabel,
            machineProviderLabel = machineProviderLabel,
            onMachineTranslate = onMachineTranslate,
            machineTranslating = machineTranslating,
        )
        PlaceholderText(
            value = value,
            modifier = SourceTextStyle.toModifier().fillMaxWidth(),
            warningPlaceholders = warningPlaceholders,
            direction = direction,
            lang = lang,
        )
    }
}

@Composable
internal fun EditorTargetField(
    keyId: String,
    category: PluralCategory?,
    value: String,
    onValueChange: (String) -> Unit,
    readOnly: Boolean = false,
    label: String?,
    placeholder: String,
    accessibleLabel: String? = null,
    error: String? = null,
    codeLike: Boolean = false,
    /**
     * The findings whose `fragment` should be underlined inside the field.
     *
     * `InlineQaFinding.fragment` has been computed on every check since the model was written and
     * consumed by nothing but a `distinctBy` key — the underline it exists for was never drawn.
     */
    qaFindings: List<InlineQaFinding> = emptyList(),
    /** BCP-47 tag of the target locale. Picks the script face; see FontStack. */
    lang: String? = null,
) {
    val categoryId = category?.name ?: "singular"
    val inputId = remember(keyId, categoryId) { "lingvo-editor-target-input-$categoryId" }
    val errorId = "$inputId-error"
    Column(
        EditorTargetFieldStyle.toModifier()
            .fillMaxWidth()
            .minWidth(0.px)
            // Per category, because every form is on screen at once now. A constant id repeated
            // across four fields is invalid markup and, worse, makes the persistent-focus lookup
            // resolve any of them to the same ref — losing the category the caret is actually in.
            // The singular case keeps the bare id, which is what the focus tables reference.
            .attr("id", if (category == null) EDITOR_TARGET_ID else "$EDITOR_TARGET_ID-$categoryId")
            .attr("data-editor-target", "true")
            .attr("data-editor-text-context", "target")
            .attr("data-editor-key-id", keyId)
            .attr("data-editor-plural-category", category?.name ?: "")
            .attr("data-editor-locale", lang.orEmpty())
            .attr("dir", "auto")
            .then(lang?.let { Modifier.attr("lang", it) } ?: Modifier),
    ) {
        label?.let { visibleLabel ->
            Label(
                forId = inputId,
                attrs = EditorTargetLabelStyle.toModifier().toAttrs(),
            ) {
                Text(visibleLabel)
            }
        }
        // The input and its underline layer, stacked. See `EditorTargetMirrorStyle` for why the
        // text is drawn twice and what has to stay identical between the two.
        Div(EditorTargetMirrorHostStyle.toModifier().toAttrs()) {
            Div(
                EditorTargetMirrorStyle.toModifier()
                    .then(if (codeLike) Modifier.monoFont() else Modifier)
                    .toAttrs {
                        attr("data-editor-target-mirror", "true")
                        attr("aria-hidden", "true")
                        attr("dir", "auto")
                        lang?.let { attr("lang", it) }
                    },
            ) {
                QaMarkedText(value, qaFindings)
            }
            TextArea(
                value = value,
                attrs = EditorTargetTextAreaStyle.toModifier()
                .then(if (codeLike) Modifier.monoFont() else Modifier)
                .styleModifier {
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("min-width", "0")
                    // min-height/max-height live once, on EditorTargetTextAreaStyle itself, rather
                    // than duplicated here.
                    property("overflow-y", "auto")
                    property("position", "relative")
                    // Transparent, so the underline layer beneath shows through. The field's fill
                    // moves to the host wrapper, which is the same box.
                    property("background-color", "transparent")
                }
                .toAttrs {
                    id(inputId)
                    placeholder(placeholder)
                    attr("dir", "auto")
                    lang?.let { attr("lang", it) }
                    accessibleLabel?.let { attr("aria-label", it) }
                    if (readOnly) {
                        // Native readonly deliberately remains focusable and selectable. A viewer
                        // can inspect or copy the server target without the field pretending that
                        // an edit will be accepted.
                        attr("readonly", "")
                    }
                    if (error != null) {
                        attr("aria-invalid", "true")
                        attr("aria-describedby", errorId)
                    }
                    if (!readOnly) {
                        onInput { onValueChange(it.value) }
                    }
                    // The mirror does not scroll with the textarea on its own; a long string
                    // scrolled under a static underline layer is the drift this whole thing exists
                    // to avoid.
                    addEventListener("scroll") { event ->
                        val area = event.target as? HTMLTextAreaElement
                        val host = area?.parentElement
                        val mirror = host?.querySelector("[data-editor-target-mirror]") as? HTMLElement
                        if (area != null && mirror != null) {
                            mirror.style.setProperty("transform", "translateY(${-area.scrollTop}px)")
                        }
                    }
                },
            )
        }
        error?.let { message ->
            Div(EditorTargetErrorStyle.toModifier().toAttrs {
                attr("id", errorId)
                attr("role", "alert")
            }) {
                Text(message)
            }
        }
    }
}

/**
 * The field's text, with every QA fragment wrapped so it can be underlined.
 *
 * Fragments are matched by literal search rather than by offset because that is all
 * [InlineQaFinding] carries, and a fragment the reader has since edited away simply finds no match
 * and marks nothing — which is the right failure. Overlapping matches are resolved by taking the
 * earliest, then continuing after it, so a mark can never be nested inside another.
 *
 * The trailing newline matters: a textarea whose value ends in "\n" reserves a final empty line,
 * and a mirror that does not reproduce it is one line short and scrolls differently.
 */
@Composable
private fun QaMarkedText(value: String, findings: List<InlineQaFinding>) {
    val marks = remember(value, findings) { qaFragmentRanges(value, findings) }
    var cursor = 0
    marks.forEach { mark ->
        if (mark.start > cursor) Text(value.substring(cursor, mark.start))
        Span(
            (
                if (mark.severity == QaSeverity.ERROR) {
                    QaUnderlineStyle.toModifier()
                } else {
                    QaUnderlineWarningStyle.toModifier()
                }
                ).toAttrs(),
        ) {
            Text(value.substring(mark.start, mark.end))
        }
        cursor = mark.end
    }
    if (cursor < value.length) Text(value.substring(cursor))
    // **The trailing-newline guard, emitted for the whole field rather than for the last chunk.**
    // A textarea whose value ends in a newline reserves a final empty line; `white-space: pre-wrap`
    // does not, unless something follows the break. Without this the mirror is exactly one line
    // short, every underline below the fold sits a line too high, and — because the earlier version
    // of this guard hung off the last text chunk — it silently did nothing whenever a QA fragment
    // happened to run to the end of the value.
    //
    // Not `t(...)`. This is a layout guard, not copy: it was minted as a catalogue row a translator
    // could edit, and the generated English already read `"\\u200b"`: six literal characters, not
    // U+200B. English never saw it (`I18n.translate` short-circuits on `en`), but the first
    // non-English catalogue to pick the key up would have printed that escape into the mirror and
    // pushed every underline below it out of registration with the textarea.
    if (value.endsWith("\n")) Text("\u200b")
}

internal data class QaFragmentRange(val start: Int, val end: Int, val severity: QaSeverity)

internal fun qaFragmentRanges(
    value: String,
    findings: List<InlineQaFinding>,
): List<QaFragmentRange> {
    if (value.isEmpty()) return emptyList()
    val ranges = mutableListOf<QaFragmentRange>()
    findings.forEach { finding ->
        val fragment = finding.fragment?.takeIf { it.isNotEmpty() } ?: return@forEach
        var from = 0
        while (true) {
            val at = value.indexOf(fragment, from)
            if (at < 0) break
            ranges += QaFragmentRange(at, at + fragment.length, finding.severity)
            from = at + fragment.length
        }
    }
    // Earliest first, errors winning a tie so a fragment flagged twice underlines as the worse of
    // the two; then drop anything that overlaps a mark already taken.
    val ordered = ranges.sortedWith(compareBy({ it.start }, { if (it.severity == QaSeverity.ERROR) 0 else 1 }))
    val result = mutableListOf<QaFragmentRange>()
    var boundary = 0
    ordered.forEach { range ->
        if (range.start >= boundary) {
            result += range
            boundary = range.end
        }
    }
    return result
}
